import {
	type UseMutationOptions,
	useMutation,
	useQueryClient
} from '@tanstack/react-query'
import type { Experience } from '@/features/experience-shell/types'
import { errorHandler } from '@/helpers/utils'
import { QUERY_KEYS } from '@/query-client'
import {
	type HttpUpdateExperienceVettingImageIsApprovedOptions,
	httpUpdateExperienceVettingImageIsApproved
} from '../http/experience-vetting.http'

export const useUpdateExperienceVettingImageIsApproved = (
	urlParams: HttpUpdateExperienceVettingImageIsApprovedOptions['urlParams'],
	options?: UseMutationOptions<
		unknown,
		unknown,
		HttpUpdateExperienceVettingImageIsApprovedOptions['payload']
	>
) => {
	const queryClient = useQueryClient()
	return useMutation({
		...options,
		mutationFn: (payload) =>
			httpUpdateExperienceVettingImageIsApproved({
				urlParams: { ...urlParams },
				payload,
				params: {
					responseType: 'small'
				}
			}),
		onError: (error, variables, onMutateResult, context) => {
			errorHandler(error)
			options?.onError?.(error, variables, onMutateResult, context)
		},
		onMutate: async (variables) => {
			const queryKey = [
				QUERY_KEYS.EXPERIENCE_VETTING,
				urlParams.experienceId
			]

			// Cancel any outgoing refetches (so they don't overwrite our optimistic update)
			await queryClient.cancelQueries({ queryKey })

			// Snapshot previous values
			const previousData = queryClient.getQueryData<Experience>(queryKey)

			// Update cached data
			if (previousData) {
				queryClient.setQueriesData<Experience>(
					{ queryKey },
					{
						...previousData,
						gallery: {
							...previousData.gallery,
							images: previousData.gallery.images.map((image) =>
								image.id === urlParams.imageId
									? {
											...image,
											isApproved: variables.isApproved
										}
									: image
							)
						}
					}
				)
			}
			return previousData
		}
	})
}
